import { postReport } from '../../../../services/api.js';
import { reportFilterFields, responseTimeField, timeWindowField } from '../../../../utils/reportRequestBody.js';
/**
 * hourlySummaryApi.js — API functions for the Hourly Summary Report page.
 *
 * fetchHourlySummaryReport — runs the hourly summary report query
 *
 * All other shared functions (filters, notes, reasons) are re-exported
 * from alertDetailsApi.js to avoid code duplication.
 */

// Re-export shared functions from alertDetailsApi

export {
    fetchFilterOptions,
    fetchPreformNotes,
    addEventNote,
    editEventReason,
} from '../../alert-details/api/alertDetailsApi.js';


// ---------------------------------------------------------------------------
// Fetch Hourly Summary Report
// ---------------------------------------------------------------------------

/**
 * Runs the hourlySummary report query with all filter fields.
 *
 * Uses a direct POST to the report endpoint with rep_type: "hourlysummary".
 *
 * @param {object} opts
 * @param {number} opts.campusId
 * @param {string} opts.username
 * @param {string} opts.startDate   — ISO format (yyyy-mm-dd hh:mm:ss)
 * @param {string} opts.endDate     — ISO format
 * @param {object} opts.filters     — all report-level filter selections
 * @returns {Promise<object>} — { records, summary, max, min, avg, savg, mostActive, overlimit, url, ErrorCode }
 */
export async function fetchHourlySummaryReport({ campusId, username, startDate, endDate, filters = {}, isCloud = false }) {
    const body = {
        rep_type: 'hourlysummary',
        /* Selects which event proc the report Lambda runs: reason 0 is a real
           service period on a cloud campus and ambiguous on a non-cloud one, and
           on cloud an auto-cleared alert (-1) is not a response at all. See
           docs/REPORT_PROC_AUDIT_PLAN.md section 3.

           Load-bearing, not cosmetic: the Lambda defaults this to falsey, so if it
           is ever dropped a cloud campus silently gets the non-cloud proc -- a
           wrong number with no error. */
        is_cloud: isCloud ? 1 : 0,
        username: username || '',
        campus_id: campusId,
        start_date: startDate,
        end_date: endDate,
        event_category_filter: '',
        ...reportFilterFields(filters),
        resp_filter_items: responseTimeField(filters),
        // Minutes from midnight, half-open [start, end). [0, 1440] is all day.
        time_window: timeWindowField(filters),
    };

    const res = await postReport(body, '[hourlySummaryApi] fetchHourlySummaryReport error');
    if (res.ErrorCode) return { ErrorCode: res.ErrorCode };
    const data = res.data;

    return {
        records: data?.records || [],
        summary: data?.summary || [],
        max: data?.max ?? null,
        min: data?.min ?? null,
        avg: data?.avg ?? null,
        savg: data?.savg ?? null,
        mostActive: data?.mostActive || [],
        overlimit: Boolean(data?.overlimit),
        url: data?.url || '',
    };
}
